import React, { useState, useEffect, useCallback } from 'react';
import { documentService } from '../../services/api';
import ConfirmDialog from '../common/ConfirmDialog';
import Notification from '../common/Notification';
import useNotification from '../../hooks/useNotification';
import './DocumentQA.css';

const DocumentQA = ({ documentId }) => {
  const [question, setQuestion] = useState('');
  const [qaHistory, setQaHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, title: '', message: '', onConfirm: null, variant: 'warning', confirmText: 'Confirm' });
  const { notification, showError, hideNotification } = useNotification();
  const MAX_QUESTION_LENGTH = 2000;

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const response = await documentService.getQaHistory(documentId);
      setQaHistory(response.data.history || []);
      setStats({
        totalQuestions: response.data.total_questions
      });
    } catch (err) {
      console.error('Failed to load Q&A history:', err);
    } finally {
      setHistoryLoading(false);
    }
  }, [documentId]);

  // Load Q&A history on mount
  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const askQuestion = async (e) => {
    e.preventDefault();
    if (!question.trim()) return;

    setLoading(true);
    setError(null);

    try {
      const response = await documentService.askQuestion(documentId, question.trim());

      // Add new Q&A to history
      setQaHistory(prev => [response.data, ...prev]);

      // Update stats
      if (stats) {
        setStats({
          totalQuestions: stats.totalQuestions + 1
        });
      }

      // Clear input
      setQuestion('');
    } catch (err) {
      console.error('Failed to ask question:', err);
      setError(err.response?.data?.detail || 'Failed to get answer. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const clearHistory = () => {
    setConfirmDialog({
      isOpen: true,
      title: 'Clear Q&A History',
      message: 'Clear all Q&A history for this document? This cannot be undone.',
      variant: 'warning',
      confirmText: 'Clear History',
      onConfirm: async () => {
        try {
          await documentService.clearQaHistory(documentId);
          setQaHistory([]);
          setStats(null);
        } catch (err) {
          console.error('Failed to clear history:', err);
          showError('Failed to clear history');
        }
      },
    });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  return (
    <div className="document-qa">
      <div className="qa-header">
        <h3>Ask Questions About This Document</h3>
        <p className="qa-description">
          Ask questions about the document content using AI.
        </p>
      </div>

      {/* Statistics */}
      {stats && stats.totalQuestions > 0 && (
        <div className="qa-stats">
          <div className="stat">
            <span className="stat-label">Questions Asked:</span>
            <span className="stat-value">{stats.totalQuestions}</span>
          </div>
          <button onClick={clearHistory} className="clear-history-btn" title="Clear Q&A History">
            Clear History
          </button>
        </div>
      )}

      {/* Question Input Form */}
      <form onSubmit={askQuestion} className="qa-form">
        <div className="question-input-group">
          <textarea
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder="Ask a question about this document..."
            className="question-input"
            disabled={loading}
            maxLength={MAX_QUESTION_LENGTH}
            rows={3}
          />
          <button
            type="submit"
            disabled={loading || !question.trim()}
            className="ask-button"
          >
            {loading ? 'Asking...' : 'Ask'}
          </button>
        </div>
        <div className="character-count" style={{
          textAlign: 'right',
          fontSize: '0.75rem',
          color: question.length > MAX_QUESTION_LENGTH * 0.9 ? '#dc2626' : '#6b7280',
          marginTop: '0.25rem'
        }}>
          {question.length} / {MAX_QUESTION_LENGTH} characters
        </div>
        {error && <div className="qa-error">{error}</div>}
      </form>

      {/* Q&A History */}
      <div className="qa-history">
        {historyLoading ? (
          <div className="loading">Loading history...</div>
        ) : qaHistory.length === 0 ? (
          <div className="empty-state">
            <p>No questions asked yet. Ask your first question above!</p>
          </div>
        ) : (
          qaHistory.map((qa, index) => (
            <div key={qa.id} className="qa-item">
              <div className="qa-header-row">
                <div className="qa-meta">
                  <span className="qa-position">Question #{qaHistory.length - index}</span>
                  <span className="qa-date">{formatDate(qa.created_at)}</span>
                </div>
              </div>
              <div className="qa-question">
                <strong>Q:</strong> {qa.question}
              </div>
              <div className="qa-answer">
                <strong>A:</strong> {qa.answer}
              </div>
            </div>
          ))
        )}
      </div>
      <Notification {...notification} onClose={hideNotification} />
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog(prev => ({ ...prev, isOpen: false }))}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        variant={confirmDialog.variant}
        confirmText={confirmDialog.confirmText}
      />
    </div>
  );
};

export default DocumentQA;
